import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from 'react-query'
import { useAuth } from '../../context/AuthContext'
import { useBranding } from '../../context/BrandingContext'
import { useTheme } from '../../context/ThemeContext'
import { apiService } from '../../services/api'
import { Icon } from '../shared/ns-icons'

// Pick the logo variant that reads well on the current sidebar background.
// logoUrl = the primary (light/white) logo for dark surfaces; logoUrlLight = dark logo for light surfaces.
function pickLogo(branding, isDark) {
  if (isDark) return branding.logoUrl || branding.logoUrlLight
  return branding.logoUrlLight || branding.logoUrl
}

// Default WebNest mark, used when no custom logo has been uploaded.
const DEFAULT_LOGO = (
  <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
    <defs>
      <linearGradient id="wn-grad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#A78BFA"/>
        <stop offset="100%" stopColor="#5B7CFF"/>
      </linearGradient>
    </defs>
    <rect width="24" height="24" rx="7" fill="url(#wn-grad)"/>
    <path d="M8 8.5c1.6-2 6.4-2 8 0M8 12c1.6-2 6.4-2 8 0M8 15.5c1.6-2 6.4-2 8 0" stroke="white" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
)

const COLLAPSE_ICON = (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m11 17-5-5 5-5M18 17l-5-5 5-5"/>
  </svg>
)

// ── Nav item definitions (icons match the prototype's shared icon set 1:1) ─────

const ADMIN_ITEMS = [
  { label: 'Dashboard',     route: '/admin',           icon: 'dashboard', exact: true },
  { label: 'Clients',       route: '/admin/clients',   icon: 'users',    badgeKey: 'clients' },
  { label: 'Sites',         route: '/admin/sites',     icon: 'globe',    badgeKey: 'sites' },
  { label: 'Visual Editor', route: '/admin/neweditor', icon: 'edit' },
  { label: 'Media Library', route: '/admin/media',     icon: 'image' },
  { label: 'Requests',      route: '/admin/requests',  icon: 'inbox',    badgeKey: 'requests' },
  { label: 'Analytics',     route: '/admin/analytics', icon: 'chart' },
  { label: 'Edit Logs',     route: '/admin/edit-logs', icon: 'activity' },
  { label: 'Backups',       route: '/admin/backups',   icon: 'archive' },
  { label: 'Domains',       route: '/admin/domains',   icon: 'domain' },
  { label: 'Billing',       route: '/admin/billing',   icon: 'card' },
  { label: 'Team',          route: '/admin/users',     icon: 'user' },
  { label: 'Settings',      route: '/admin/settings',  icon: 'settings' },
]

const CLIENT_ITEMS = [
  { label: 'Home',         route: '/client',           icon: 'dashboard', exact: true },
  { label: 'My sites',     route: '/client/sites',     icon: 'globe' },
  { label: 'Edit my site', route: '/client/editor',    icon: 'edit' },
  { label: 'Requests',     route: '/client/requests',  icon: 'message', badgeKey: 'requests' },
  { label: 'Analytics',    route: '/client/analytics', icon: 'chart' },
  { label: 'Domain',       route: '/client/domains',   icon: 'domain' },
  { label: 'Billing',      route: '/client/billing',   icon: 'card' },
  { label: 'Help',         route: '/client/support',   icon: 'help' },
  { label: 'Settings',     route: '/client/settings',  icon: 'settings' },
]

// ── Component ─────────────────────────────────────────────────────────────────

export default function Sidebar({ collapsed, onCollapse, mobileOpen }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const branding = useBranding()
  const { isDark } = useTheme()
  const isAdmin = user?.role === 'admin'
  const logoSrc = pickLogo(branding, isDark)

  const items = isAdmin ? ADMIN_ITEMS : CLIENT_ITEMS

  // Live counts shown as nav badges. Reuses the ['clients']/['sites'] query keys
  // so it dedupes with the manager screens' caches.
  const { data: clientsData } = useQuery(
    ['clients'],
    () => apiService.get('/clients').then(r => r.data),
    { staleTime: 30000, enabled: isAdmin, refetchOnWindowFocus: false }
  )
  const { data: sitesData } = useQuery(
    ['sites'],
    () => apiService.get('/sites').then(r => r.data),
    { staleTime: 30000, enabled: isAdmin, refetchOnWindowFocus: false }
  )
  const { data: requestStats } = useQuery(
    ['request-stats'],
    () => apiService.get('/requests/stats/overview').then(r => r.data),
    { staleTime: 30000, enabled: isAdmin, refetchOnWindowFocus: false }
  )
  const { data: clientPending } = useQuery(
    ['client-pending-requests'],
    () => apiService.get('/requests', { params: { status: 'pending' } })
      .then(r => (Array.isArray(r.data) ? r.data.length : 0)),
    { staleTime: 30000, enabled: !isAdmin, refetchOnWindowFocus: false }
  )

  const counts = isAdmin
    ? {
        clients: clientsData?.clients?.length,
        sites: sitesData?.sites?.length,
        requests: requestStats?.statusBreakdown?.pending,
      }
    : { requests: clientPending }

  function isActive(item) {
    if (item.exact || item.route === '/admin' || item.route === '/client') {
      return location.pathname === item.route || location.pathname === item.route + '/'
    }
    return location.pathname.startsWith(item.route)
  }

  return (
    <aside className={`sidebar${mobileOpen ? ' mobile-open' : ''}`} style={{ width: collapsed ? 68 : 248 }}>
      <div className="sidebar-inner">

        {/* Logo / workspace */}
        <div className="ws-switcher">
          <div className="ws-logo">
            {logoSrc
              ? <img src={logoSrc} alt={branding.cmsName} style={{ width: 28, height: 28, objectFit: 'contain', borderRadius: 7 }} />
              : DEFAULT_LOGO}
          </div>
          {!collapsed && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="ws-name">{branding.cmsName}</div>
              <div className="ws-meta">{isAdmin ? 'Admin workspace' : 'Client portal'}</div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {!collapsed && (
            <div className="nav-section-label">{isAdmin ? 'Workspace' : 'Your site'}</div>
          )}
          {items.map((item) => {
            const active = isActive(item)
            const count = item.badgeKey ? counts[item.badgeKey] : undefined
            const showBadge = typeof count === 'number' && count > 0
            return (
              <button
                key={item.route}
                className={`nav-item${active ? ' active' : ''}`}
                onClick={() => navigate(item.route)}
                title={collapsed ? item.label : undefined}
                style={{ justifyContent: collapsed ? 'center' : undefined }}
              >
                <span className="nav-icon"><Icon name={item.icon} /></span>
                {!collapsed && <span style={{ flex: 1 }}>{item.label}</span>}
                {!collapsed && showBadge && (
                  <span className={`nav-badge${active ? ' on' : ''}`}>{count}</span>
                )}
              </button>
            )
          })}
        </nav>

        <div style={{ flex: 1 }} />

        {/* Legal links at bottom */}
        {!collapsed && (
          <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 10, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px 2px' }}>
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="legal-link">Privacy</a>
            <span style={{ color: 'var(--text-faint)', fontSize: 11 }}>·</span>
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="legal-link">Terms</a>
          </div>
        )}

        {/* Collapse button */}
        <button
          className="collapse-btn"
          onClick={onCollapse}
          title={collapsed ? 'Expand' : 'Collapse'}
          style={{ transform: collapsed ? 'rotate(180deg)' : undefined }}
        >
          {COLLAPSE_ICON}
        </button>

      </div>
    </aside>
  )
}
